/**
 * @deprecated This file is maintained for backward compatibility.
 * Please import from the new modular structure:
 * - Extensions: import from './media/media-extensions'
 * - Media type detection: import from './media/media-type-detector'
 * - File operations: import from './file/extension-utils'
 * - File size formatting: import from './file/file-size-formatter'
 * - Image conversion: import from './image/image-converter'
 */

import { ToTypes } from '../types/types';
import * as MEDIA from './media/media-extensions';

// Re-export from new modules for backward compatibility
export {
    IMAGE_EXTENSIONS,
    AUDIO_EXTENSIONS,
    VIDEO_EXTENSIONS,
    ARCHIVE_EXTENSIONS,
    DOCUMENT_EXTENSIONS,
    EBOOK_EXTENSIONS,
    PRESENTATION_EXTENSIONS,
    VECTOR_EXTENSIONS,
    PDF_EXTENSIONS as PDF_EXTENSION,
    PS_EXTENSIONS as PS_EXTENSION,
    MS_OFFICE_EXTENSIONS as MS_OFFICE_EXTENSION,
    ALL_EXTENSIONS,
    ALL_EXTENSIONS_SET,
    THUMBNAIL_SUPPORTED_EXTENSIONS,
    DISALLOWED_EXTENSIONS as DISALLOWED_EXTENSTIONS,
} from './media/media-extensions';

export {
    isVideoToType,
    isAudioToType,
    isImageToType,
    isVideoFromType,
    isAudioFromType,
    isImageFromType,
} from './media/media-type-detector';

export {
    getExtension,
    getExtensionSimple as getExtensionFallback,
    getExtensionList,
    isKnownSupportedExtension,
    isExtensionDisallowed as isExtensionAllowed,
    isThumbnailSupported,
} from './file/extension-utils';

export {
    toReadableBytes,
    toReadableBits,
} from './file/file-size-formatter';

export {
    fileToJpgImage,
    fileToTransparentPngImage,
} from './image/image-converter';

/**
 * @deprecated Use imports from './media/media-extensions' instead.
 * This class is maintained for backward compatibility only.
 */
export class FileUtils {
    /** @deprecated Use IMAGE_EXTENSIONS from './media/media-extensions' (the lists below are those, from xconfig) */
    static IMAGE_EXTENSIONS: string[] = [...MEDIA.IMAGE_EXTENSIONS];

    /** @deprecated Use AUDIO_EXTENSIONS from './media/media-extensions' */
    static AUDIO_EXTENSIONS: string[] = [...MEDIA.AUDIO_EXTENSIONS];

    /** @deprecated Use VIDEO_EXTENSIONS from './media/media-extensions' */
    static VIDEO_EXTENSIONS: string[] = [...MEDIA.VIDEO_EXTENSIONS];

    /** @deprecated Use ARCHIVE_EXTENSIONS from './media/media-extensions' */
    static ARCHIVE_EXTENSIONS: string[] = [...MEDIA.ARCHIVE_EXTENSIONS];

    /** @deprecated Use DOCUMENT_EXTENSIONS from './media/media-extensions' */
    static DOCUMENT_EXTENSIONS: string[] = [...MEDIA.DOCUMENT_EXTENSIONS];

    /** @deprecated Use EBOOK_EXTENSIONS from './media/media-extensions' */
    static EBOOK_EXTENSIONS: string[] = [...MEDIA.EBOOK_EXTENSIONS];

    /** @deprecated Use PRESENTATION_EXTENSIONS from './media/media-extensions' */
    static PRESENTATION_EXTENSIONS: string[] = [...MEDIA.PRESENTATION_EXTENSIONS];

    /** @deprecated Use VECTOR_EXTENSIONS from './media/media-extensions' */
    static VECTOR_EXTENSIONS: string[] = [...MEDIA.VECTOR_EXTENSIONS];

    /** @deprecated Use MS_OFFICE_EXTENSIONS from './media/media-extensions' */
    static MS_OFFICE_EXTENSION: string[] = [...MEDIA.MS_OFFICE_EXTENSIONS];

    static PDF_EXTENSION: string[] = [...MEDIA.PDF_EXTENSIONS];
    static PS_EXTENSION: string[] = [...MEDIA.PS_EXTENSIONS];

    static ALL_EXTENSIONS: string[] = [...MEDIA.ALL_EXTENSIONS];

    static ALL_EXTENSIONS_SET = new Set(FileUtils.ALL_EXTENSIONS);

    static DISALLOWED_EXTENSTIONS = new Set([
        "ADE", "ADP", "APK", "APPX", "APPXBUNDLE", "BAT", "CAB", "CHM", "CMD", "COM", "CPL", "DIAGCAB", "DIAGCFG",
        "DIAGPACK", "DLL", "DMG", "EX", "EX_", "EXE", "HTA", "IMG", "INS", "ISO", "ISP", "JAR", "JNLP", "JS", "JSE",
        "LIB", "LNK", "MDE", "MSC", "MSI", "MSIX", "MSIXBUNDLE", "MSP", "MST", "NSH", "PIF", "PS1", "SCR", "SCT",
        "SHB", "SYS", "VB", "VBE", "VBS", "VHD", "VXD", "WSC", "WSF", "WSH", "XLL", "BAK", "BCK", "BKP", "CFG",
        "CONF", "CONFIG", "INI", "LOG", "OLD", "SAV", "SAVE", "TEMP", "TMP", "BIN", "CGI", "SHTM", "SHTML", "STM",
        "CER", "CRT", "DER", "KEY", "P12", "P7B", "P7C", "PEM", "PFX", "DAT", "EML", "HTR", "HTW", "IDA", "IDC", "IDQ",
        "NWS", "POL", "PRINTER", "REG", "WMZ"
    ]);

    static THUMBNAIL_SUPPORTED_EXTENSIONS: string[] = [...MEDIA.THUMBNAIL_SUPPORTED_EXTENSIONS];

    /** @deprecated Use isVideoToType from './media/media-type-detector' */
    public static isVideoToType(toType: string) {
        toType = toType.toLowerCase();
        return toType === ToTypes.VIDEO || FileUtils.VIDEO_EXTENSIONS.map(t => t.toLowerCase()).includes(toType);
    }

    /** @deprecated Use isAudioToType from './media/media-type-detector' */
    public static isAudioToType(toType: string) {
        toType = toType.toLowerCase();
        return toType === ToTypes.AUDIO || FileUtils.AUDIO_EXTENSIONS.map(t => t.toLowerCase()).includes(toType);
    }

    /** @deprecated Use isImageToType from './media/media-type-detector' */
    public static isImageToType(toType: string) {
        toType = toType.toLowerCase();
        return toType === ToTypes.IMAGE || FileUtils.IMAGE_EXTENSIONS.map(t => t.toLowerCase()).includes(toType);
    }

    /** @deprecated Use isVideoFromType from './media/media-type-detector' */
    public static isVideoFromType(fromTypes: string[]) {
        const supportedFromTypes = [ToTypes.VIDEO, ...FileUtils.VIDEO_EXTENSIONS].map(t => t.toLowerCase());
        fromTypes = fromTypes.map(t => t.toLowerCase());
        return fromTypes.some(fromType => supportedFromTypes.includes(fromType));
    }

    /** @deprecated Use isAudioFromType from './media/media-type-detector' */
    public static isAudioFromType(fromTypes: string[]) {
        const supportedFromTypes = [ToTypes.AUDIO, ...FileUtils.AUDIO_EXTENSIONS].map(t => t.toLowerCase());
        fromTypes = fromTypes.map(t => t.toLowerCase());
        return fromTypes.some(fromType => supportedFromTypes.includes(fromType));
    }

    /** @deprecated Use isImageFromType from './media/media-type-detector' */
    public static isImageFromType(fromTypes: string[]) {
        const supportedFromTypes = [ToTypes.IMAGE, ...FileUtils.IMAGE_EXTENSIONS].map(t => t.toLowerCase());
        fromTypes = fromTypes.map(t => t.toLowerCase());
        return fromTypes.some(fromType => supportedFromTypes.includes(fromType));
    }

    /** @deprecated Use fileToJpgImage from './image/image-converter' */
    public static fileToJpgImage(file: File): Promise<HTMLImageElement> {
        const { fileToJpgImage } = require('./image/image-converter');
        return fileToJpgImage(file);
    }

    /** @deprecated Use fileToTransparentPngImage from './image/image-converter' */
    public static fileToTransparentPngImage(file: File): Promise<HTMLImageElement> {
        const { fileToTransparentPngImage } = require('./image/image-converter');
        return fileToTransparentPngImage(file);
    }

    /** @deprecated Use getExtensionSimple from './file/extension-utils' */
    public static getExtensionFallback(fileName: string) {
        if (fileName.lastIndexOf('.') === -1) {
            return fileName;
        }
        return fileName.substring(fileName.lastIndexOf('.') + 1);
    }

    /** @deprecated Use getExtension from './file/extension-utils' */
    public static getExtension(fileName: string) {
        const extension = FileUtils.ARCHIVE_EXTENSIONS.find(ext => fileName.toUpperCase().endsWith(ext));
        return extension ? fileName.substring(fileName.length - extension.length)
            : FileUtils.getExtensionFallback(fileName);
    }

    /** @deprecated Use getExtensionList from './file/extension-utils' */
    public static getExtensionList(filenames: string[]): string[] {
        return filenames.map(filename => FileUtils.getExtension(filename));
    }

    /** @deprecated Use isKnownSupportedExtension from './file/extension-utils' */
    public static isKnownSupportedExtension(filename: string) {
        return FileUtils.ALL_EXTENSIONS.indexOf(FileUtils.getExtension(filename).toUpperCase()) !== -1;
    }

    /** @deprecated Use isExtensionDisallowed from './file/extension-utils' */
    public static isExtensionAllowed(filename: string): boolean {
        return FileUtils.DISALLOWED_EXTENSTIONS.has(FileUtils.getExtension(filename).toUpperCase());
    }

    /** @deprecated Use isThumbnailSupported from './file/extension-utils' */
    public static isThumbnailSupported(filename: string) {
        const extension = FileUtils.getExtension(filename).toUpperCase();
        return FileUtils.THUMBNAIL_SUPPORTED_EXTENSIONS.indexOf(extension) !== -1;
    }

    /** @deprecated Use toReadableBytes from './file/file-size-formatter' */
    public static toReadableBytes(value: number, unitType: 'rate' | 'quantity') {
        const unitBase = unitType == 'quantity' ? 'B' : 'Bps';
        const n = parseInt(value + "", 10);
        if (n > 1000000000) {
            return parseFloat(`${n / 1000000000}`).toFixed(2) + ` G${unitBase}`;
        }
        if (n > 1000000) {
            return parseFloat(`${n / 1000000}`).toFixed(2) + ` M${unitBase}`;
        }
        if (n > 1000) {
            return parseFloat(`${n / 1000}`).toFixed(2) + ` K${unitBase}`;
        }
        return n + (unitType == 'quantity' ? ' Bytes' : 'Bps');
    }

    /** @deprecated Use toReadableBits from './file/file-size-formatter' */
    public static toReadableBits(bits: number, unitType: 'rate' | 'quantity') {
        const unitBase = unitType == 'quantity' ? 'b' : 'bps';
        const n = parseInt(bits + "", 10);
        if (n > 1000000000) {
            return parseFloat(`${n / 1000000000}`).toFixed(2) + ` G${unitBase}`;
        }
        if (n > 1000000) {
            return parseFloat(`${n / 1000000}`).toFixed(2) + ` M${unitBase}`;
        }
        if (n > 1000) {
            return parseFloat(`${n / 1000}`).toFixed(2) + ` K${unitBase}`;
        }
        return unitType == 'quantity' ? `${n} bits` : `${n} bps`;
    }
}
